import { supabase } from '../lib/supabase';

/**
 * Upload Service - Handles file uploads and material management
 */
const uploadService = {
  /**
   * Upload a file to Supabase Storage and create database record
   * @param {File} file - The file to upload
   * @param {string} fileType - 'script', 'training_material', 'objection_guide', 'other'
   * @param {string} teamId - The team ID
   * @param {string} description - Optional description
   * @param {string[]} tags - Optional tags
   * @returns {Promise<Object>} Upload record
   */
  async uploadFile(file, fileType, teamId, description = '', tags = []) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${teamId}/${fileType}/${fileName}`;

      // Upload file to Supabase Storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from('training-materials')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (storageError) throw storageError;

      // Create database record
      const { data: uploadRecord, error: dbError } = await supabase
        .from('uploaded_materials')
        .insert({
          team_id: teamId,
          uploader_id: user.id,
          file_name: file.name,
          file_path: storageData.path,
          file_size: file.size,
          file_type: fileType,
          mime_type: file.type,
          description: description,
          tags: tags,
          processing_status: 'pending'
        })
        .select()
        .single();

      if (dbError) {
        // Rollback storage upload if DB insert fails
        await supabase.storage.from('training-materials').remove([filePath]);
        throw dbError;
      }

      return uploadRecord;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },

  /**
   * Get all uploads for a team
   * @param {string} teamId - The team ID
   * @returns {Promise<Array>} List of uploads
   */
  async getTeamUploads(teamId) {
    try {
      const { data, error } = await supabase
        .from('uploaded_materials')
        .select('*')
        .eq('team_id', teamId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching uploads:', error);
      throw error;
    }
  },

  /**
   * Get upload by ID
   * @param {string} uploadId - The upload ID
   * @returns {Promise<Object>} Upload record
   */
  async getUploadById(uploadId) {
    try {
      const { data, error } = await supabase
        .from('uploaded_materials')
        .select('*')
        .eq('id', uploadId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching upload:', error);
      throw error;
    }
  },

  /**
   * Download file from storage
   * @param {string} filePath - The file path in storage
   * @returns {Promise<Blob>} File blob
   */
  async downloadFile(filePath) {
    try {
      const { data, error } = await supabase.storage
        .from('training-materials')
        .download(filePath);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },

  /**
   * Get public URL for file (if needed)
   * @param {string} filePath - The file path in storage
   * @returns {string} Public URL
   */
  getFileUrl(filePath) {
    const { data } = supabase.storage
      .from('training-materials')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  /**
   * Update upload processing status
   * @param {string} uploadId - The upload ID
   * @param {string} status - 'processing', 'completed', 'failed'
   * @param {Object} updates - Additional updates (extracted_text, metadata, error, etc.)
   * @returns {Promise<Object>} Updated record
   */
  async updateProcessingStatus(uploadId, status, updates = {}) {
    try {
      const updateData = {
        processing_status: status,
        ...updates
      };

      if (status === 'completed') {
        updateData.processed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('uploaded_materials')
        .update(updateData)
        .eq('id', uploadId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating processing status:', error);
      throw error;
    }
  },

  /**
   * Delete upload (soft delete)
   * @param {string} uploadId - The upload ID
   * @returns {Promise<void>}
   */
  async deleteUpload(uploadId) {
    try {
      // Soft delete - set is_active to false
      const { error } = await supabase
        .from('uploaded_materials')
        .update({ is_active: false })
        .eq('id', uploadId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting upload:', error);
      throw error;
    }
  },

  /**
   * Permanently delete upload and file
   * @param {string} uploadId - The upload ID
   * @returns {Promise<void>}
   */
  async permanentlyDeleteUpload(uploadId) {
    try {
      // Get upload record to find file path
      const upload = await this.getUploadById(uploadId);

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('training-materials')
        .remove([upload.file_path]);

      if (storageError) console.error('Storage deletion error:', storageError);

      // Delete from database
      const { error: dbError } = await supabase
        .from('uploaded_materials')
        .delete()
        .eq('id', uploadId);

      if (dbError) throw dbError;
    } catch (error) {
      console.error('Error permanently deleting upload:', error);
      throw error;
    }
  },

  /**
   * Get upload statistics for team
   * @param {string} teamId - The team ID
   * @returns {Promise<Object>} Statistics
   */
  async getUploadStats(teamId) {
    try {
      const { data, error } = await supabase
        .from('uploaded_materials')
        .select('file_type, processing_status, file_size')
        .eq('team_id', teamId)
        .eq('is_active', true);

      if (error) throw error;

      const stats = {
        total: data.length,
        byType: {},
        byStatus: {},
        totalSize: 0
      };

      data.forEach(upload => {
        // Count by type
        stats.byType[upload.file_type] = (stats.byType[upload.file_type] || 0) + 1;
        
        // Count by status
        stats.byStatus[upload.processing_status] = (stats.byStatus[upload.processing_status] || 0) + 1;
        
        // Sum file sizes
        stats.totalSize += upload.file_size;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching upload stats:', error);
      throw error;
    }
  }
};

export default uploadService;
